/**
 * Typed wrapper for the agent's `/api/userdata/*` server-first storage
 * surface (F4). Lets the web app read
 * and write namespaced user-data blobs (test results, parameter logs,
 * tasks, prefs, calendar, dosing schedule, …) that live on the Pi and
 * therefore survive a browser clear, a device switch, and a `pi-deploy`.
 *
 * Routes covered here:
 *   GET /api/userdata             → { ok, namespaces: string[] }
 *   GET /api/userdata/:namespace  → { ok, data: unknown }
 *   PUT /api/userdata/:namespace  (body = full namespace blob) → { ok }
 */
import { agentFetch } from './agent-fetch';
import { loadPublicEnv } from './env';

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

function baseUrl(): string {
  return loadPublicEnv().agentDevUrl.replace(/\/+$/, '');
}

async function request<T>(path: string, init?: RequestInit): Promise<Result<T>> {
  try {
    const res = await agentFetch(`${baseUrl()}${path}`, {
      ...init,
      headers: {
        'content-type': 'application/json',
        ...(init?.headers ?? {}),
      },
    });
    if (!res.ok) {
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        // ignore parse failure — fall through to status text
      }
      const msg =
        body &&
        typeof body === 'object' &&
        'error' in body &&
        typeof (body as { error: unknown }).error === 'string'
          ? (body as { error: string }).error
          : `HTTP ${res.status} ${res.statusText}`;
      return { ok: false, error: msg };
    }
    const value = (await res.json()) as T;
    return { ok: true, value };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

export const userDataApi = {
  /** GET /api/userdata — list every namespace currently stored. */
  async list(): Promise<Result<{ namespaces: string[] }>> {
    const r = await request<{ ok: true; namespaces: string[] }>(
      '/api/userdata',
    );
    if (!r.ok) return r;
    return { ok: true, value: { namespaces: r.value.namespaces } };
  },

  /**
   * GET /api/userdata/:namespace — read one namespace blob. `data` is
   * `null` when the namespace has never been written.
   */
  async get(namespace: string): Promise<Result<{ data: unknown }>> {
    const r = await request<{ ok: true; data: unknown }>(
      `/api/userdata/${encodeURIComponent(namespace)}`,
    );
    if (!r.ok) return r;
    return { ok: true, value: { data: r.value.data } };
  },

  /**
   * PUT /api/userdata/:namespace — overwrite one namespace blob with the
   * full value. The agent persists atomically (tmp+rename).
   */
  async put(namespace: string, blob: unknown): Promise<Result<{ ok: true }>> {
    const r = await request<{ ok: true }>(
      `/api/userdata/${encodeURIComponent(namespace)}`,
      { method: 'PUT', body: JSON.stringify(blob) },
    );
    if (!r.ok) return r;
    return { ok: true, value: { ok: true } };
  },
};
